import { useParams } from 'react-router'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { CreateButton } from 'src/shared/button/Buttons'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Section } from 'src/shared/presentation/Section'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { KapProgramJournalForm } from 'src/screens/kap/program/journal/edit/KapProgramJournalForm'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from 'urql'
import { Kap_Program, Query_Root } from 'src/@types/graphql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import {
  fetchKapProgramByIdQuery,
  queryModulesOfProgramsInCanton,
} from 'src/screens/kap/program/details/kapProgramQueries'
import { MODULE_TYPES } from 'src/shared/constants/constants'

export const KapProgramJournalCreatePage = () => {
  const { programId } = useParams()
  const programIdInt = parseInt(programId as string)
  const { getMessage } = useMessageSource()

  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [kapProgram, setKapProgram] = useState<Kap_Program>()
  const [modulesInCanton, setModulesInCanton] = useState<Set<MODULE_TYPES>>(new Set())

  const onBack = useCallback(
    (kapJournalId?: number) => {
      const route = ROUTES.KapProgramJournal.params({ programId: programId })

      if (kapJournalId) {
        navigate(`${route}#${kapJournalId}`, { state: { expandedJournalId: kapJournalId } })
      } else {
        navigate(route)
      }
    },
    [programId, navigate],
  )

  const fetchKapProgramData = useCallback(async (): Promise<Kap_Program> => {
    const { data } = await urqlClient
      .query<{ kap_program_by_pk: Query_Root['kap_program_by_pk'] }>(fetchKapProgramByIdQuery, {
        id: programIdInt,
      })
      .toPromise()

    if (!data?.kap_program_by_pk) {
      throw new Error('Resource not found')
    }

    return data.kap_program_by_pk
  }, [programIdInt, urqlClient])

  const fetchKapProgramsInCantonWithModulesData = useCallback(
    async (fetchedKapProgram: Kap_Program) => {
      const { data } = await urqlClient
        .query<{ kap_program: Query_Root['kap_program'] }>(queryModulesOfProgramsInCanton, {
          canton_code: fetchedKapProgram.canton_code,
        })
        .toPromise()

      if (!data?.kap_program) {
        throw new Error('Resource not found')
      }

      return data.kap_program
    },
    [urqlClient],
  )

  const initData = useCallback(async () => {
    try {
      const kapProgramData = await fetchKapProgramData()
      const programsInCanton = await fetchKapProgramsInCantonWithModulesData(kapProgramData)

      setKapProgram(kapProgramData)

      const uniqueFetchedModules = new Set<MODULE_TYPES>()
      programsInCanton.forEach((program) => {
        program.modules.forEach((module: MODULE_TYPES) => uniqueFetchedModules.add(module))
      })

      setModulesInCanton(uniqueFetchedModules)
    } catch (err) {
      notificationService.operationFailed()
    }
  }, [fetchKapProgramData, fetchKapProgramsInCantonWithModulesData, notificationService])

  useEffect(() => {
    if (programIdInt) {
      initData()
    }
  }, [programIdInt, initData])

  const onSubmit = () => {
    document.getElementById('kap-journal-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const { canEditJournals, loading } = usePermissionsForKapProgram(parseInt(programId as string))

  if (!loading && !canEditJournals) {
    return <NotAuthorized inEditPage />
  }

  return (
    <ScreenLayout
      title={getMessage('label.add.program.journal')}
      actions={<CreateButton origin="header" onClick={onSubmit} />}
      onBack={() => onBack()}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        <Section helpAndInstructions={<HelpAndInstructions labelKey="label.help.program.journal" defaultExpansion />}>
          {kapProgram && modulesInCanton && (
            <KapProgramJournalForm afterSubmit={onBack} kapProgram={kapProgram} modulesInCanton={modulesInCanton} />
          )}
        </Section>
      </PageLayout>
    </ScreenLayout>
  )
}
